import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Heart, Check } from "lucide-react";

/* ─── DESIGN TOKENS ─── */
const C = {
  black:    "#0A0A0A",
  dark:     "#111111",
  red:      "#F50000",
  redDark:  "#C00000",
  orange:   "#FF4A00",
  amber:    "#FF8A00",
  gold:     "#D88A00",
  white:    "#FFFFFF",
  offWhite: "#F5F3EF",
  gray:     "#888888",
  border:   "rgba(0,0,0,0.08)",
  textDark: "#1A1A1A",
  textMid:  "#555555",
};

const BRAND_GRADIENT      = "linear-gradient(90deg,#D88A00,#FF8A00,#FF4A00,#F50000)";
const BRAND_GRADIENT_FULL = "linear-gradient(135deg,#0E0A01 0%,#5A3600 25%,#D88A00 50%,#FF4A00 75%,#F50000 100%)";
const RED_GRADIENT        = "linear-gradient(135deg,#8B0000 0%,#C00000 40%,#F50000 100%)";
const HERO_BG             = "linear-gradient(135deg,#0A0000 0%,#1A0000 40%,#2D0000 70%,#1A0000 100%)";

const fontHref = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Outfit:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = fontHref;
  document.head.appendChild(l);
}

/* ─── PRESET AMOUNTS ─── */
const PRESETS = [5, 10, 25, 50, 100, 250];

/* ─── CAUSES ─── */
const CAUSES = [
  {
    id: "tech-access",
    icon: "💻",
    title: "Digital Access",
    body: "Funding devices and internet access for underserved communities so no one is left behind by the digital economy.",
  },
  {
    id: "youth-education",
    icon: "🎓",
    title: "Youth Education",
    body: "Supporting coding bootcamps, scholarships, and mentorship programmes for young people entering the tech industry.",
  },
  {
    id: "sustainability",
    icon: "🌱",
    title: "Sustainability",
    body: "Investing in green infrastructure and carbon offset programmes aligned with 4REVA GROUP's long-term ESG commitments.",
  },
  {
    id: "group",
    icon: "⚡",
    title: "Where Needed Most",
    body: "Let 4REVA GROUP allocate your donation to the area of greatest current need across our charitable initiatives.",
  },
];

/* ═══════════════════════
   HEART PULSE ICON
═══════════════════════ */
function PulsingHeart() {
  return (
    <motion.div
      animate={{ scale: [1, 1.15, 1] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
      style={{
        width: 80, height: 80, borderRadius: "50%",
        background: RED_GRADIENT,
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 32px",
        boxShadow: "0 0 40px rgba(245,0,0,0.3)",
      }}
    >
      <Heart size={36} color={C.white} fill={C.white} />
    </motion.div>
  );
}

/* ═══════════════════════
   CAUSE CARD
═══════════════════════ */
function CauseCard({
  cause,
  selected,
  onSelect,
  index,
}: {
  cause: typeof CAUSES[0];
  selected: boolean;
  onSelect: () => void;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      onClick={onSelect}
      style={{
        borderRadius: 16,
        padding: "28px 24px",
        cursor: "pointer",
        background: selected ? "rgba(245,0,0,0.08)" : "#131313",
        border: selected ? `1.5px solid ${C.red}` : "1.5px solid rgba(255,255,255,0.07)",
        transition: "all 0.25s ease",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {selected && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: "absolute", top: 12, right: 12,
            width: 22, height: 22, borderRadius: "50%",
            background: C.red,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Check size={12} color={C.white} strokeWidth={3} />
        </motion.div>
      )}
      <div style={{ fontSize: 28, marginBottom: 14 }}>{cause.icon}</div>
      <h4 style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 20, fontWeight: 600,
        color: selected ? C.white : "rgba(255,255,255,0.8)",
        margin: "0 0 10px",
      }}>
        {cause.title}
      </h4>
      <p style={{
        fontSize: 13, lineHeight: 1.75,
        color: selected ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.38)",
        margin: 0, fontFamily: "'Outfit', sans-serif",
        transition: "color 0.25s",
      }}>
        {cause.body}
      </p>
    </motion.div>
  );
}

/* ═══════════════════════
   MAIN PAGE
═══════════════════════ */
export default function DonatePage() {
  const navigate = useNavigate();
  const [scrolled,    setScrolled]    = useState(false);
  const [amount,      setAmount]      = useState<number | "">(25);
  const [customInput, setCustomInput] = useState("");
  const [frequency,   setFrequency]   = useState<"once" | "monthly">("once");
  const [cause,       setCause]       = useState("group");
  const [name,        setName]        = useState("");
  const [email,       setEmail]       = useState("");
  const [submitted,   setSubmitted]   = useState(false);
  const [submitting,  setSubmitting]  = useState(false);

  React.useEffect(() => {
    const h = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const finalAmount = customInput ? parseFloat(customInput) : (amount || 0);

  const handleSubmit = async () => {
    if (!finalAmount || finalAmount < 1 || !email) return;
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 1400));
    setSubmitting(false);
    setSubmitted(true);
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    padding: "14px 18px",
    color: C.white,
    fontSize: 14,
    fontFamily: "'Outfit', sans-serif",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.4)",
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    display: "block",
    marginBottom: 8,
  };

  return (
    <>
      <Helmet>
        <title>Donate — 4REVA GROUP</title>
        <meta name="description" content="Support 4REVA GROUP's charitable initiatives — digital access, youth education, and sustainability. Every contribution makes a difference." />
      </Helmet>

      <div style={{ backgroundColor: C.black, minHeight: "100vh", fontFamily: "'Outfit', sans-serif", overflowX: "hidden" }}>

        {/* ── NAV ── */}
        <motion.nav
          animate={{
            backgroundColor: scrolled ? "rgba(10,10,10,0.96)" : "transparent",
            backdropFilter: scrolled ? "blur(16px)" : "blur(0px)",
          }}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
            padding: "0 40px", height: 72,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
          }}
        >
          <motion.button
            whileHover={{ x: -3 }}
            onClick={() => navigate("/")}
            style={{ background: "none", border: "none", color: C.white, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontFamily: "'Outfit', sans-serif", opacity: 0.7 }}
          >
            <ArrowLeft size={16} /> Back to Group
          </motion.button>

          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, letterSpacing: "0.22em", color: C.white, fontWeight: 600 }}>
            4REVA GROUP
          </div>

          {/* Pulsing heart badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
              style={{ color: C.red }}
            >
              <Heart size={16} fill={C.red} />
            </motion.div>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.08em" }}>
              DONATE
            </span>
          </div>
        </motion.nav>

        {/* ── HERO ── */}
        <section style={{
          background: HERO_BG,
          minHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          padding: "140px 6% 80px",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Red glow orb */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.12, 0.22, 0.12] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute", top: "10%", right: "8%",
              width: 700, height: 700, borderRadius: "50%",
              background: "radial-gradient(circle, rgba(245,0,0,0.18) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <motion.div
            animate={{ scale: [1, 1.15, 1], opacity: [0.08, 0.16, 0.08] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 3 }}
            style={{
              position: "absolute", bottom: "20%", left: "15%",
              width: 500, height: 500, borderRadius: "50%",
              background: "radial-gradient(circle, rgba(200,0,0,0.15) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Accent line — red */}
          <div style={{ position: "absolute", top: 0, left: 0, width: 5, height: "100%", background: RED_GRADIENT }} />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <PulsingHeart />

            <motion.p
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
              style={{
                fontSize: 10, letterSpacing: "0.45em", fontWeight: 700,
                textTransform: "uppercase", marginBottom: 20,
                color: C.red, fontFamily: "'Outfit', sans-serif",
                textAlign: "center",
              }}
            >
              4REVA GROUP · CHARITABLE GIVING
            </motion.p>

            <h1 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(48px, 6.5vw, 96px)",
              fontWeight: 300,
              lineHeight: 1.03,
              margin: "0 0 10px",
              color: C.white,
              textAlign: "center",
            }}>
              Make a{" "}
              <em style={{ color: C.red, fontStyle: "italic", fontWeight: 400 }}>
                difference.
              </em>
            </h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
              style={{
                fontSize: 16, lineHeight: 1.8,
                color: "rgba(255,255,255,0.52)",
                maxWidth: 520, margin: "24px auto 48px",
                fontFamily: "'Outfit', sans-serif",
                textAlign: "center",
              }}
            >
              Every contribution supports 4REVA GROUP's charitable initiatives — from digital access and youth education to sustainability and community impact.
            </motion.p>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <motion.button
                whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
                onClick={() => document.querySelector("#donate-form")?.scrollIntoView({ behavior: "smooth" })}
                style={{
                  background: RED_GRADIENT, color: C.white, border: "none",
                  padding: "18px 52px", fontWeight: 700, fontSize: 12,
                  letterSpacing: "0.18em", cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif", borderRadius: 4,
                  display: "flex", alignItems: "center", gap: 10,
                  boxShadow: "0 8px 32px rgba(245,0,0,0.3)",
                }}
              >
                DONATE NOW <Heart size={14} fill={C.white} />
              </motion.button>
            </div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.7 }}
            style={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}
          >
            <div style={{ width: 1, height: 40, background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.2))" }} />
            <span style={{ fontSize: 9, letterSpacing: "0.35em", color: "rgba(255,255,255,0.25)", textTransform: "uppercase", fontFamily: "'Outfit', sans-serif" }}>Scroll</span>
          </motion.div>
        </section>

        {/* ── IMPACT STATS ── */}
        <div style={{ background: RED_GRADIENT, padding: "22px 6%", display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 20 }}>
          {[
            { num: "100%", label: "Goes to Causes" },
            { num: "4",    label: "Active Initiatives" },
            { num: "EU",   label: "Regulated & Transparent" },
            { num: "∞",    label: "Impact Potential" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 34, color: C.white, lineHeight: 1, fontWeight: 600 }}>{s.num}</div>
              <div style={{ fontSize: 10, letterSpacing: "0.22em", color: "rgba(255,255,255,0.75)", textTransform: "uppercase", marginTop: 4, fontFamily: "'Outfit', sans-serif" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── CAUSES ── */}
        <section style={{ padding: "100px 6%", background: "#0C0C0C" }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginBottom: 56, textAlign: "center" }}>
            <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 14px", color: C.red, fontFamily: "'Outfit', sans-serif" }}>
              Our Initiatives
            </p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(34px, 4vw, 56px)", fontWeight: 400, margin: 0, color: C.white, lineHeight: 1.1 }}>
              Where your donation goes
            </h2>
          </motion.div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
            {CAUSES.map((c, i) => (
              <CauseCard
                key={c.id}
                cause={c}
                selected={cause === c.id}
                onSelect={() => setCause(c.id)}
                index={i}
              />
            ))}
          </div>
        </section>

        {/* ── DONATE FORM ── */}
        <section id="donate-form" style={{ padding: "100px 6%", background: C.black, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginBottom: 52, textAlign: "center" }}>
              <p style={{ fontSize: 10, letterSpacing: "0.42em", fontWeight: 700, textTransform: "uppercase", margin: "0 0 16px", color: C.red, fontFamily: "'Outfit', sans-serif" }}>
                Give Today
              </p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(34px, 4vw, 52px)", fontWeight: 400, margin: "0 0 16px", color: C.white, lineHeight: 1.1 }}>
                Your contribution
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              style={{
                background: "#111111",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 20,
                padding: "48px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Red top border accent */}
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: RED_GRADIENT }} />

              {submitted ? (
                /* ── SUCCESS STATE ── */
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={{ textAlign: "center", padding: "40px 0" }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 18 }}
                    style={{
                      width: 72, height: 72, borderRadius: "50%",
                      background: RED_GRADIENT,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      margin: "0 auto 28px",
                      boxShadow: "0 0 40px rgba(245,0,0,0.3)",
                    }}
                  >
                    <Heart size={30} color={C.white} fill={C.white} />
                  </motion.div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, color: C.white, margin: "0 0 14px", fontWeight: 400 }}>
                    Thank You{name ? `, ${name.split(" ")[0]}` : ""}
                  </h3>
                  <p style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", lineHeight: 1.75, maxWidth: 400, margin: "0 auto 8px", fontFamily: "'Outfit', sans-serif" }}>
                    Your {frequency === "monthly" ? "monthly " : ""}donation of{" "}
                    <span style={{ color: C.white, fontWeight: 600 }}>£{finalAmount}</span>{" "}
                    has been received. A confirmation will be sent to {email}.
                  </p>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", fontFamily: "'Outfit', sans-serif", marginTop: 20 }}>
                    4REVA GROUP · Charitable Giving · Reg. No. 17381555
                  </p>
                </motion.div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>

                  {/* Frequency toggle */}
                  <div>
                    <label style={labelStyle}>Frequency</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      {(["once", "monthly"] as const).map(f => (
                        <button
                          key={f}
                          onClick={() => setFrequency(f)}
                          style={{
                            flex: 1,
                            padding: "12px 0",
                            borderRadius: 8,
                            border: frequency === f ? `1.5px solid ${C.red}` : "1.5px solid rgba(255,255,255,0.1)",
                            background: frequency === f ? "rgba(245,0,0,0.1)" : "transparent",
                            color: frequency === f ? C.white : "rgba(255,255,255,0.4)",
                            fontSize: 12,
                            fontWeight: 600,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            cursor: "pointer",
                            fontFamily: "'Outfit', sans-serif",
                            transition: "all 0.2s",
                          }}
                        >
                          {f === "once" ? "One-time" : "Monthly"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Amount presets */}
                  <div>
                    <label style={labelStyle}>Amount (GBP)</label>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 12 }}>
                      {PRESETS.map(p => (
                        <motion.button
                          key={p}
                          whileTap={{ scale: 0.96 }}
                          onClick={() => { setAmount(p); setCustomInput(""); }}
                          style={{
                            padding: "14px 0",
                            borderRadius: 8,
                            border: amount === p && !customInput
                              ? `1.5px solid ${C.red}`
                              : "1.5px solid rgba(255,255,255,0.1)",
                            background: amount === p && !customInput
                              ? "rgba(245,0,0,0.1)"
                              : "transparent",
                            color: amount === p && !customInput ? C.white : "rgba(255,255,255,0.5)",
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "'Cormorant Garamond', serif",
                            transition: "all 0.2s",
                          }}
                        >
                          £{p}
                        </motion.button>
                      ))}
                    </div>

                    {/* Custom amount */}
                    <div style={{ position: "relative" }}>
                      <span style={{
                        position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)",
                        color: "rgba(255,255,255,0.4)", fontSize: 16, fontFamily: "'Outfit', sans-serif",
                        pointerEvents: "none",
                      }}>£</span>
                      <input
                        type="number"
                        placeholder="Other amount"
                        value={customInput}
                        onChange={e => { setCustomInput(e.target.value); setAmount(""); }}
                        style={{ ...inputStyle, paddingLeft: 30 }}
                        onFocus={e => e.target.style.borderColor = C.red}
                        onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
                      />
                    </div>
                  </div>

                  {/* Cause selector (mini) */}
                  <div>
                    <label style={labelStyle}>Direct to</label>
                    <select
                      value={cause}
                      onChange={e => setCause(e.target.value)}
                      style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}
                      onFocus={e => e.target.style.borderColor = C.red}
                      onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
                    >
                      {CAUSES.map(c => (
                        <option key={c.id} value={c.id} style={{ background: "#1A1A1A" }}>{c.title}</option>
                      ))}
                    </select>
                  </div>

                  {/* Name + Email */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={labelStyle}>Name (optional)</label>
                      <input
                        type="text"
                        placeholder="Your name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        style={inputStyle}
                        onFocus={e => e.target.style.borderColor = C.red}
                        onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Email *</label>
                      <input
                        type="email"
                        placeholder="For your receipt"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        style={inputStyle}
                        onFocus={e => e.target.style.borderColor = C.red}
                        onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"}
                      />
                    </div>
                  </div>

                  {/* Summary + Submit */}
                  <div>
                    {/* Summary pill */}
                    {finalAmount > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                          background: "rgba(245,0,0,0.08)",
                          border: `1px solid rgba(245,0,0,0.2)`,
                          borderRadius: 10,
                          padding: "14px 20px",
                          marginBottom: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", fontFamily: "'Outfit', sans-serif" }}>
                          {frequency === "monthly" ? "Monthly donation" : "One-time donation"} → {CAUSES.find(c => c.id === cause)?.title}
                        </span>
                        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, color: C.white, fontWeight: 600 }}>
                          £{finalAmount}{frequency === "monthly" ? "/mo" : ""}
                        </span>
                      </motion.div>
                    )}

                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={handleSubmit}
                      disabled={submitting || finalAmount < 1 || !email}
                      style={{
                        width: "100%",
                        background: finalAmount < 1 || !email ? "rgba(255,255,255,0.06)" : RED_GRADIENT,
                        color: C.white,
                        border: "none",
                        padding: "18px 0",
                        fontWeight: 700,
                        fontSize: 13,
                        letterSpacing: "0.18em",
                        cursor: finalAmount < 1 || !email ? "not-allowed" : "pointer",
                        fontFamily: "'Outfit', sans-serif",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                        opacity: submitting ? 0.7 : 1,
                        transition: "background 0.3s",
                        boxShadow: finalAmount >= 1 && email ? "0 8px 28px rgba(245,0,0,0.25)" : "none",
                      }}
                    >
                      {submitting ? "PROCESSING…" : `DONATE £${finalAmount || "—"}${frequency === "monthly" ? "/MONTH" : ""}`}
                      {!submitting && <Heart size={15} fill={C.white} />}
                    </motion.button>

                    <p style={{ marginTop: 14, fontSize: 11, color: "rgba(255,255,255,0.25)", fontFamily: "'Outfit', sans-serif", lineHeight: 1.7, textAlign: "center" }}>
                      Secure processing · 100% goes to the cause · You'll receive a confirmation email
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </section>

        {/* ── FOOTER MINI ── */}
        <footer style={{ background: "#080808", borderTop: "1px solid rgba(255,255,255,0.06)", padding: "32px 6%", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", margin: 0, fontFamily: "'Outfit', sans-serif" }}>
            © {new Date().getFullYear()} 4REVA GROUP®. Charitable giving programme. Reg. No. 17381555
          </p>
          <div style={{ display: "flex", gap: 24 }}>
            {[
              { label: "Privacy", path: "/privacy" },
              { label: "Terms",   path: "/terms" },
              { label: "Home",    path: "/" },
            ].map(l => (
              <span
                key={l.label}
                onClick={() => navigate(l.path)}
                style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", cursor: "pointer", fontFamily: "'Outfit', sans-serif", transition: "color 0.2s" }}
                onMouseEnter={e => (e.currentTarget.style.color = C.red)}
                onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.35)")}
              >
                {l.label}
              </span>
            ))}
          </div>
        </footer>
      </div>

      <style>{`
        @media (max-width: 600px) {
          #donate-form > div > div:last-child > div {
            padding: 28px 20px !important;
          }
          #donate-form input, #donate-form select {
            font-size: 16px !important;
          }
        }
      `}</style>
    </>
  );
}
